import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Row, Col, Card, Divider, Switch, Image, Checkbox, Select, Modal, Tooltip, Tabs, Progress, Alert, InputNumber, Tour, message } from 'antd';
import { QuestionCircleOutlined, CopyOutlined, SyncOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_ACCOUNT, UPDATE_ACCOUNT, GET_PLUGCHAT_STATUS, DISCONNECT_PLUGCHAT, GET_EVOLUTION_STATUS, DISCONNECT_EVOLUTION, GENERATE_PIPEDRIVE_CREDENTIALS, START_WHATSAPP_MASS_VERIFICATION, GET_WHATSAPP_VERIFICATION_JOB_STATUS, GET_AI_PROMPT_QUESTIONS, SAVE_AI_PROMPT_QUESTION_ANSWER, GENERATE_ACCOUNT_PROMPT_TEMPLATE } from '../mutations/account';
import { SEARCH_PRICE_SEGMENTATIONS } from '../mutations/priceSegmentations.js';
import { SEARCH_CITIES } from '../mutations/address';
import { GET_PIPEDRIVE_STAGES } from '../mutations/pipedrive';
import AddressForm from '../components/AddressForm';
import CampaignHoursSettings from '../components/CampaignHoursSettings';
import useDebounce from '../hooks/useDebounce';

const AccountSettings = () => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();
  const [loadAccount, { loading: queryLoading, data }] = useLazyQuery(GET_ACCOUNT, { fetchPolicy: 'network-only' });
  const [storeAccount, { loading: mutationLoading }] = useMutation(UPDATE_ACCOUNT);
  const [loadPlugchatStatus, { data: plugchatData }] = useLazyQuery(GET_PLUGCHAT_STATUS, { fetchPolicy: 'network-only' });
  const [disconnectPlugchat] = useMutation(DISCONNECT_PLUGCHAT);
  const [loadEvolutionStatus, { data: evolutionData }] = useLazyQuery(GET_EVOLUTION_STATUS, { fetchPolicy: 'network-only' });
  const [disconnectEvolution] = useMutation(DISCONNECT_EVOLUTION);
  const [searchPriceSegmentations, { data: priceSegmentationsData }] = useLazyQuery(SEARCH_PRICE_SEGMENTATIONS, {
    fetchPolicy: 'network-only'
  });
  const [searchCities, { data: citiesData }] = useLazyQuery(SEARCH_CITIES, {
    fetchPolicy: 'network-only'
  });
  const [loadPipedriveStages, { data: pipedriveStagesData, loading: pipedriveStagesLoading, error: pipedriveStagesError }] = useLazyQuery(GET_PIPEDRIVE_STAGES, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all', // Permite que a query continue mesmo com erros
    onError: (error) => {
      console.error('Erro ao carregar stages do Pipedrive:', error);
      // Não mostrar message.error aqui para evitar spam de erros
    }
  });
  const [generatePipedriveCredentials, { loading: generatingCredentials }] = useMutation(GENERATE_PIPEDRIVE_CREDENTIALS);

  // WhatsApp Mass Verification states and hooks
  const [verificationJob, setVerificationJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [isVerificationRunning, setIsVerificationRunning] = useState(false);

  const [startMassVerification, { loading: startingVerification }] = useMutation(START_WHATSAPP_MASS_VERIFICATION);
  const [getJobStatus, { data: jobStatusData }] = useLazyQuery(GET_WHATSAPP_VERIFICATION_JOB_STATUS, {
    fetchPolicy: 'network-only',
    pollInterval: isVerificationRunning ? 5000 : 0 // Poll a cada 5 segundos se estiver rodando
  });

  const [qrCodeImage, setQrCodeImage] = useState('');
  const [plugChatConnected, setPlugChatConnected] = useState(false);
  const [connectedNumber, setConnectedNumber] = useState('');
  const [disconnecting, setDisconnecting] = useState(false);

  // Evolution API states
  const [evolutionQrCodeImage, setEvolutionQrCodeImage] = useState('');
  const [evolutionConnected, setEvolutionConnected] = useState(false);
  const [evolutionConnectedNumber, setEvolutionConnectedNumber] = useState('');
  const [evolutionDisconnecting, setEvolutionDisconnecting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [priceSegmentations, setPriceSegmentations] = useState([]);
  const [priceSegmentationSearchTerm, setPriceSegmentationSearchTerm] = useState('');
  const [cities, setCities] = useState([]);
  const [pipedriveStages, setPipedriveStages] = useState([]);
  const debouncedSearchTerm = useDebounce(priceSegmentationSearchTerm, 1000);

  const location = useLocation();
  const [form] = Form.useForm();
  const [promptAssistantForm] = Form.useForm();
  const whatsappRef = useRef(null);
  const aiPromptRef = useRef(null);
  const campaignsRef = useRef(null);
  const serviceRef = useRef(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);
  const [promptAssistantOpen, setPromptAssistantOpen] = useState(false);
  const [promptAssistantField, setPromptAssistantField] = useState(null);
  const [promptAssistantLabel, setPromptAssistantLabel] = useState('');
  const [promptQuestions, setPromptQuestions] = useState([]);
  const [loadPromptQuestions, { loading: loadingPromptQuestions }] = useLazyQuery(GET_AI_PROMPT_QUESTIONS, { fetchPolicy: 'network-only' });
  const [savePromptAnswer] = useMutation(SAVE_AI_PROMPT_QUESTION_ANSWER);
  const [generateAccountPromptTemplate, { loading: generatingPromptTemplate }] = useMutation(GENERATE_ACCOUNT_PROMPT_TEMPLATE);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    const step = location.state?.onboardingStep;
    const account = data?.account;
    if (!step || !account) return;

    const hasValue = (value) => typeof value === 'string' && value.trim().length > 0;
    const stepAlreadyConfigured = {
      connect_whatsapp: account.whatsappProvider === 'evolution' ? evolutionConnected : plugChatConnected,
      configure_ai_prompt: hasValue(account.aiPromptTemplate),
      configure_campaign_prompts: [
        account.campaignOfferPromptTemplate,
        account.campaignReactivationPromptTemplate,
        account.campaignActivationPromptTemplate,
        account.campaignFollowUpPromptTemplate
      ].some(hasValue),
      configure_service_prompt: hasValue(account.servicePromptTemplate)
    };

    if (stepAlreadyConfigured[step]) {
      setTourOpen(false);
      return;
    }

    const stepsMap = {
      connect_whatsapp: {
        title: 'Conecte o WhatsApp',
        description: 'Para enviar campanhas, conecte o WhatsApp da conta. Confira o status em Conectado e, se ainda não estiver conectado, escaneie o QR Code para concluir a conexão.',
        target: () => whatsappRef.current,
        placement: 'bottom'
      },
      configure_ai_prompt: {
        title: 'Configure as instruções da IA',
        description: 'Preencha o campo de instruções gerais para personalização de mensagens. Use o botão "Configurar com assistente" para gerar automaticamente.',
        target: () => aiPromptRef.current,
        placement: 'bottom'
      },
      configure_campaign_prompts: {
        title: 'Configure os prompts de campanha',
        description: 'Preencha os prompts para campanhas de oferta, reativação, ativação e follow-up. Use o assistente em cada campo para gerar textos personalizados.',
        target: () => campaignsRef.current,
        placement: 'bottom'
      },
      configure_service_prompt: {
        title: 'Configure a IA de Serviços',
        description: 'Preencha as instruções para qualificação de leads de serviços. Use o botão de template padrão ou o assistente para começar.',
        target: () => serviceRef.current,
        placement: 'bottom'
      }
    };

    const tourStep = stepsMap[step];
    if (tourStep) {
      setTourSteps([tourStep]);
      setTourOpen(true);
    }
  }, [data?.account, evolutionConnected, location.state?.onboardingStep, plugChatConnected]);

  // Carrega status baseado no provider
  useEffect(() => {
    if (data?.account?.whatsappProvider === 'evolution') {
      loadEvolutionStatus();
    } else {
      loadPlugchatStatus();
    }
  }, [data?.account?.whatsappProvider, loadPlugchatStatus, loadEvolutionStatus]);

  useEffect(() => {
    if (debouncedSearchTerm.length > 2 || debouncedSearchTerm === '*') {
      searchPriceSegmentations({ variables: { query: debouncedSearchTerm } });
    }
  }, [debouncedSearchTerm, searchPriceSegmentations]);

  useEffect(() => {
    if (priceSegmentationsData) {
      setPriceSegmentations(priceSegmentationsData.searchPriceSegmentations);
    }
  }, [priceSegmentationsData]);

  useEffect(() => {
    if (citiesData) {
      setCities(citiesData.searchCities);
    }
  }, [citiesData]);

  useEffect(() => {
    if (pipedriveStagesData && !pipedriveStagesError) {
      setPipedriveStages(pipedriveStagesData.pipedriveStages || []);
    } else if (pipedriveStagesError) {
      setPipedriveStages([]); // Limpar stages em caso de erro
    }
  }, [pipedriveStagesData, pipedriveStagesError]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!plugChatConnected && data?.account?.whatsappProvider !== 'evolution') {
        loadPlugchatStatus().then((value) => {
          let plugchatData = value.data

          setPlugChatConnected(plugchatData?.plugchatStatus?.plugchatConnected)
          if (plugchatData && plugchatData?.plugchatStatus?.plugchatConnected === false) {
            setQrCodeImage(plugchatData?.plugchatStatus?.plugchatQrCode);
          }
        });
      }
    }, 20000); // 20 segundos

    return () => clearInterval(interval); // Limpa o intervalo quando o componente desmonta
  }, [plugChatConnected, loadPlugchatStatus, data]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!evolutionConnected && data?.account?.whatsappProvider === 'evolution') {
        loadEvolutionStatus().then((value) => {

          let evolutionData = value.data
          setEvolutionConnected(evolutionData?.evolutionStatus?.evolutionConnected)
          if (evolutionData && evolutionData?.evolutionStatus?.evolutionConnected === false) {
            setEvolutionQrCodeImage(evolutionData?.evolutionStatus?.evolutionQrCode);
          }
        });
      }
    }, 20000); // 20 segundos

    return () => clearInterval(interval); // Limpa o intervalo quando o componente desmonta
  }, [evolutionConnected, loadEvolutionStatus, data]);

  useEffect(() => {
    if (plugchatData) {
      setPlugChatConnected(plugchatData?.plugchatStatus?.plugchatConnected);
      setConnectedNumber(plugchatData?.plugchatStatus?.connectedNumber);
      if (!plugchatData?.plugchatStatus?.plugchatConnected) {
        setQrCodeImage(plugchatData?.plugchatStatus?.plugchatQrCode);

      }
    }
  }, [plugchatData]);

  useEffect(() => {
    if (evolutionData) {
      console.log(evolutionData)
      setEvolutionConnected(evolutionData?.evolutionStatus?.evolutionConnected);
      setEvolutionConnectedNumber(evolutionData?.evolutionStatus?.connectedNumber);
      if (!evolutionData?.evolutionStatus?.evolutionConnected) {
        setEvolutionQrCodeImage(evolutionData?.evolutionStatus?.evolutionQrCode);
      }
    }
  }, [evolutionData]);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const data = await disconnectPlugchat();
      messageApi.success('Desconectado com sucesso');
      setPlugChatConnected(false);
      setQrCodeImage('');
      setConnectedNumber('');
      window.location.reload();
    } catch (error) {
      messageApi.error('Falha ao desconectar');
      console.error(error);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleEvolutionDisconnect = async () => {
    setEvolutionDisconnecting(true);
    try {
      const data = await disconnectEvolution();
      messageApi.success('Desconectado com sucesso');
      setEvolutionConnected(false);
      setEvolutionQrCodeImage('');
      setEvolutionConnectedNumber('');
      window.location.reload();
    } catch (error) {
      messageApi.error('Falha ao desconectar');
      console.error(error);
    } finally {
      setEvolutionDisconnecting(false);
    }
  };

  const handleSearchPriceSegmentation = (value) => {
    setPriceSegmentationSearchTerm(value);
  };

  const handleSearchCity = (value) => {
    if (value.length > 2) {
      searchCities({ variables: { query: value } });
    }
  };

  const handleLoadPipedriveStages = async () => {
    const tokenPipedrive = form.getFieldValue('tokenPipedrive');

    if (!tokenPipedrive || pipedriveStagesLoading) {
      return;
    }

    // Evita recarregar se já temos dados e não houve mudança no token
    if (pipedriveStages.length > 0 && !pipedriveStagesLoading) {
      return;
    }

    try {
      await loadPipedriveStages();
    } catch (error) {
      console.error('Erro ao carregar stages do Pipedrive:', error);
      // Só mostrar erro se for uma ação manual do usuário
      if (error.message.includes('unauthorized') || error.message.includes('401')) {
        message.error('Token do Pipedrive inválido ou expirado. Verifique suas credenciais.');
      } else {
        message.error('Erro ao carregar stages do Pipedrive. Tente novamente.');
      }
    }
  };

  const handleGeneratePipedriveCredentials = async () => {
    try {
      // Verifica se temos o accountId
      if (!data?.account?.id) {
        message.error('ID da conta não encontrado');
        return;
      }

      const result = await generatePipedriveCredentials({
        variables: {
          accountId: data.account.id
        }
      });

      if (result.data?.generatePipedriveCredentials?.errors?.length > 0) {
        messageApi.error('Erro ao gerar credenciais: ' + result.data.generatePipedriveCredentials.errors.join(', '));
      } else {
        messageApi.success('Credenciais geradas com sucesso!');
        // Recarrega os dados da conta para atualizar os campos
        loadAccount();
      }
    } catch (error) {
      console.error('Erro ao gerar credenciais do Pipedrive:', error);
      messageApi.error('Erro ao gerar credenciais do Pipedrive: ' + error.message);
    }
  };

  // WhatsApp Mass Verification Functions
  const handleStartMassVerification = async () => {
    try {
      modal.confirm({
        title: 'Verificação em Massa de WhatsApp',
        content: (
          <div>
            <p>Este processo irá verificar todos os números de WhatsApp dos seus clientes que ainda não foram verificados.</p>
            <Alert
              message="Informações Importantes"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li>O processo será executado em segundo plano</li>
                  <li>Pode levar alguns minutos dependendo da quantidade de clientes</li>
                  <li>Você será notificado quando o processo for concluído</li>
                  <li>Você pode continuar usando o sistema normalmente</li>
                </ul>
              }
              type="info"
              showIcon
            />
            <p style={{ marginTop: 16, marginBottom: 0 }}>Deseja continuar?</p>
          </div>
        ),
        okText: 'Sim, iniciar verificação',
        cancelText: 'Cancelar',
        width: 500,
        onOk: async () => {
          const result = await startMassVerification();

          if (result.data?.startWhatsappMassVerification?.errors?.length > 0) {
            messageApi.error('Erro ao iniciar verificação: ' + result.data.startWhatsappMassVerification.errors.join(', '));
          } else {
            const jobData = result.data.startWhatsappMassVerification;
            setVerificationJob(jobData);
            setIsVerificationRunning(true);

            messageApi.success(`Verificação iniciada! Processando aproximadamente ${jobData.estimatedCount} clientes.`);

            // Inicia o polling do status
            getJobStatus({ variables: { jobId: jobData.jobId } });
          }
        }
      });
    } catch (error) {
      console.error('Erro ao iniciar verificação em massa:', error);
      messageApi.error('Erro ao iniciar verificação em massa: ' + error.message);
    }
  };

  // Atualiza status do job
  useEffect(() => {
    if (jobStatusData?.whatsappVerificationJobStatus) {
      const status = jobStatusData.whatsappVerificationJobStatus;
      setJobStatus(status);

      if (status.status === 'completed' || status.status === 'failed') {
        setIsVerificationRunning(false);

        if (status.status === 'completed') {
          messageApi.success(`Verificação concluída! ${status.successCount} números verificados com sucesso.`);
        } else {
          messageApi.error('Verificação falhou. Verifique os logs para mais detalhes.');
        }
      }
    }
  }, [jobStatusData, messageApi]);

  // Componente de Status da Verificação
  const renderVerificationStatus = () => {
    if (!jobStatus) return null;

    const { status, progress, processedCount, totalCount, successCount, failureCount } = jobStatus;

    const statusConfig = {
      pending: { color: 'blue', icon: <SyncOutlined spin />, text: 'Aguardando início...' },
      running: { color: 'blue', icon: <SyncOutlined spin />, text: 'Verificando números...' },
      completed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Concluído com sucesso!' },
      failed: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Falha na verificação' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Card size="small" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          {config.icon}
          <span style={{ marginLeft: 8, fontWeight: 'bold' }}>Status da Verificação: {config.text}</span>
        </div>

        {status === 'running' && (
          <>
            <Progress
              percent={Math.round(progress || 0)}
              status="active"
              format={(percent) => `${percent}% (${processedCount}/${totalCount})`}
            />
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              ✅ Verificados: {successCount} | ❌ Falhas: {failureCount}
            </div>
          </>
        )}

        {status === 'completed' && (
          <div style={{ color: 'green', fontSize: '14px' }}>
            ✅ {successCount} números verificados com sucesso
            {failureCount > 0 && ` | ❌ ${failureCount} falhas`}
          </div>
        )}
      </Card>
    );
  };

  useEffect(() => {
    if (data) {
      setQrCodeImage(data?.account?.plugchatQrCode)
      setPlugChatConnected(data?.account?.plugchatConnected)
      setConnectedNumber(data?.account?.connectedNumber);
      let cityData = undefined
      if (data.account.address && data.account.address.city) {
        cityData = {
          value: data.account.address.city.id,
          label: `${data.account.address.city.name} - ${data.account.address.city.state.initials}`
        }
        // Adiciona a cidade atual à lista de cidades disponíveis
        setCities([{
          id: data.account.address.city.id,
          name: data.account.address.city.name,
          state: data.account.address.city.state
        }]);
      }

      // Fetch the price segmentation data if we have an ID
      if (data.account.priceSegmentationId) {
        searchPriceSegmentations({ variables: { query: '*' } });
      }

      form.setFieldsValue({
        ...data.account,
        address: {
          ...data.account.address,
          cityId: cityData
        }
      });

      // Carrega stages do Pipedrive automaticamente se houver token
      if (data.account.tokenPipedrive) {
        handleLoadPipedriveStages();
      }
    }
  }, [data, form, searchPriceSegmentations]);

  // Carrega stages do Pipedrive quando o token é alterado
  useEffect(() => {
    const tokenPipedrive = form.getFieldValue('tokenPipedrive');
    if (tokenPipedrive && data?.account) {
      handleLoadPipedriveStages();
    }
  }, [form.getFieldValue('tokenPipedrive')]);

  const handleUpdate = async (values) => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const addressValues = {
      ...values.address,
      cityId: values.address?.cityId?.value || values.address?.cityId,
    };

    const { plugchatConnected, plugchatQrCode, ...filteredValues } = values;

    // Convert string fields to appropriate types
    const finalValues = {
      ...filteredValues,
      address: addressValues,
      // Convert numeric fields from string to integer
      inactiveCustomerDays: values.inactiveCustomerDays ? parseInt(values.inactiveCustomerDays, 10) : null,
      searchLimit: values.searchLimit ? parseInt(values.searchLimit, 10) : null,
      initialCode: values.initialCode ? parseInt(values.initialCode, 10) : null,
      campaignMinIntervalSeconds: values.campaignMinIntervalSeconds ? parseInt(values.campaignMinIntervalSeconds, 10) : null,
      campaignMaxIntervalSeconds: values.campaignMaxIntervalSeconds ? parseInt(values.campaignMaxIntervalSeconds, 10) : null,
      pipedriveStageId: values.pipedriveStageId ? parseInt(values.pipedriveStageId, 10) : null,
      onlyVerifiedWhatsappCampaigns: values.onlyVerifiedWhatsappCampaigns || false,
    };

    try {
      const { data } = await storeAccount({
        variables: { attributes: finalValues }
      });
      if (data.storeAccount.errors.length > 0) {
        messageApi.error(data.storeAccount.errors.join(', '));
      } else {
        messageApi.success('Conta atualizada com sucesso');
      }
    } catch (error) {
      messageApi.error('Falha ao atualizar a conta');
      console.error(error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const isSaveLoading = mutationLoading || isSubmitting;

  const handleFillServicePromptTemplate = () => {
    const currentValue = form.getFieldValue('servicePromptTemplate');

    // Se já tem conteúdo, pergunta se quer sobrescrever
    if (currentValue && currentValue.trim()) {
      Modal.confirm({
        title: 'Substituir conteúdo existente?',
        content: 'Já existe conteúdo no campo. Deseja substituir pelo template padrão?',
        okText: 'Sim, substituir',
        cancelText: 'Cancelar',
        onOk: () => fillTemplate()
      });
    } else {
      fillTemplate();
    }
  };

  const fillTemplate = () => {
    const defaultTemplate = `DESCRIÇÃO:
Você é um assistente de pré-vendas da {{company_name}}. Seu objetivo é qualificar leads para agendamento de demonstrações do nosso sistema de gestão.

COMPORTAMENTO:
- Seja cordial, profissional e direto
- Faça uma pergunta por vez
- Colete informações essenciais para qualificação
- Use o nome do cliente sempre que possível
- Mantenha o foco no objetivo: qualificar e agendar

PROCESSO DE QUALIFICAÇÃO:
1. Boas-vindas e confirmação do interesse
2. Pergunta o email de contato
3. Perguntar sobre o tipo de negócio/produtos
4. Identificar o segmento (compra/venda, industrialização, serviços)
5. Entender o tipo de vendas (varejo, atacado, ambos)
6. Quantidade de terminais PDV ou usuários
7. Método de entrega (próprio ou terceirizado)
8. Possui aplicativo de força de vendas externos?
9. Propor agendamento se qualificado

CRITÉRIOS DE QUALIFICAÇÃO:
- Empresa com operação comercial ativa
- Necessidade de sistema de gestão
- Múltiplos usuários ou terminais
- Interesse em modernizar processos
- Precisa de um aplicativo de força de vendas externo

LINK DE AGENDAMENTO: https://calendly.com/acelera-venda/demo

RESPOSTA:
- Se qualificado: inclua o link do Calendly na mensagem
- Se não qualificado: seja educado e explique que talvez nosso sistema não seja ideal
- Sempre mantenha o status e situação atualizados`;

    form.setFieldsValue({
      servicePromptTemplate: defaultTemplate
    });
  };

  const openPromptAssistant = async (referenceField, label) => {
    setPromptAssistantField(referenceField);
    setPromptAssistantLabel(label);
    setPromptAssistantOpen(true);

    try {
      const response = await loadPromptQuestions({ variables: { referenceField } });
      const questions = response.data?.aiPromptQuestions || [];
      setPromptQuestions(questions);

      if (questions.length === 0) {
        messageApi.warning('Nenhuma pergunta configurada para este tipo de prompt. Execute a carga inicial de perguntas antes de usar o assistente.');
      }

      const answers = {};
      questions.forEach((question) => {
        answers[`question_${question.id}`] = question.answer?.answer || '';
      });
      promptAssistantForm.setFieldsValue(answers);
    } catch (error) {
      messageApi.error('Falha ao carregar perguntas do assistente');
    }
  };

  const closePromptAssistant = () => {
    setPromptAssistantOpen(false);
    setPromptAssistantField(null);
    setPromptAssistantLabel('');
    setPromptQuestions([]);
    promptAssistantForm.resetFields();
  };

  const handleGeneratePromptFromAnswers = async () => {
    try {
      if (promptQuestions.length === 0) {
        messageApi.warning('Nenhuma pergunta configurada para este tipo de prompt. Execute a carga inicial de perguntas antes de gerar o prompt.');
        return;
      }

      const values = await promptAssistantForm.validateFields();

      for (const question of promptQuestions) {
        await savePromptAnswer({
          variables: {
            questionId: question.id,
            answer: values[`question_${question.id}`]
          }
        });
      }

      const response = await generateAccountPromptTemplate({
        variables: { referenceField: promptAssistantField }
      });
      const result = response.data?.generateAccountPromptTemplate;

      if (result?.errors?.length > 0) {
        messageApi.error(result.errors.join(', '));
        return;
      }

      const promptTemplate = result?.promptTemplate || '';
      form.setFieldsValue({ [promptAssistantField]: promptTemplate });
      messageApi.success('Prompt gerado com sucesso');
      closePromptAssistant();
    } catch (error) {
      if (!error.errorFields) {
        messageApi.error('Falha ao gerar prompt');
      }
    }
  };

  const promptAssistantButton = (referenceField, label) => (
    <Button
      size="small"
      type="link"
      onClick={() => openPromptAssistant(referenceField, label)}
      style={{ padding: '0 4px', height: 'auto', fontSize: '12px' }}
    >
      Configurar com assistente
    </Button>
  );

  if (queryLoading) return <p>Carregando...</p>;

  const tabItems = [
    {
      key: 'general',
      label: 'Configurações Gerais',
      children: (
        <Form
          form={form}
          initialValues={data?.account}
          onFinish={handleUpdate}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="corporateName" label="Nome da Empresa">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="name" label="Razão Social">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="document" label="Documento">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="email" label="Email">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phone" label="Telefone">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="domain" label="Dominio">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item name="initialCode" label="Código inicial">
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="currentCode" label="Código atual do pedido">
                <Input disabled={true} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item
                name="priceSegmentationId"
                label="Tabela de Preço Padrão"
                tooltip="Selecione a tabela de preço padrão a ser utilizada no sistema"
              >
                <Select
                  showSearch
                  placeholder="Pesquisar Tabela de preço"
                  onSearch={handleSearchPriceSegmentation}
                  filterOption={false}
                  options={priceSegmentations.map(priceSegmentation => ({
                    label: priceSegmentation.name,
                    value: priceSegmentation.id.toString(),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="businessType"
                label="Tipo de Negócio"
                tooltip="Selecione se sua empresa trabalha com produtos ou serviços para personalizar o atendimento"
              >
                <Select
                  placeholder="Selecione o tipo de negócio"
                  options={[
                    { label: 'Produtos', value: 'product' },
                    { label: 'Serviços', value: 'service' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider>Whatsapp da conta</Divider>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="whatsappIaServiceEnabled"
                label="Atendimento por IA ativo"
                tooltip="Ativa ou desativa o atendimento automático por IA via WhatsApp. Quando desativado, o sistema não processará mensagens recebidas."
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {/* Seção PlugChat - quando provider não é evolution */}
          {data?.account?.whatsappProvider !== 'evolution' && (
            <>
              <Row ref={whatsappRef} gutter={16}>
                <Col justify="center" align="middle">
                  <Form.Item name="plugchatConnected" label="Conectado">
                    <Checkbox disabled={true} checked={plugChatConnected} />
                  </Form.Item>
                </Col>

                {!plugChatConnected && (
                  <Col>
                    <Form.Item name="plugchatQrCode" label="QR Code">
                      <Image
                        preview={false}
                        width={200}
                        src={qrCodeImage}
                      />
                    </Form.Item>
                  </Col>
                )}
                {plugChatConnected && (
                  <>
                    <Col >
                      <Form.Item label="Número Conectado">
                        <Input value={connectedNumber} disabled />
                      </Form.Item>
                    </Col>
                    <Col >
                      <Form.Item style={{ textAlign: 'right', paddingTop: '30px' }}>
                        <Button
                          type="primary"
                          htmlType="button"
                          onClick={handleDisconnect}
                          loading={disconnecting}
                          disabled={disconnecting}
                        >
                          Desconectar PlugChat
                        </Button>
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="plugchatToken" label="Token PlugChat">
                    <Input.TextArea allowClear={true} placeholder="Digite o token do PlugChat" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* Seção Evolution - quando provider é evolution */}
          {data?.account?.whatsappProvider === 'evolution' && (
            <>
              <Row ref={whatsappRef} gutter={16}>
                <Col justify="center" align="middle">
                  <Form.Item name="evolutionConnected" label="Conectado">
                    <Checkbox disabled={true} checked={evolutionConnected} />
                  </Form.Item>
                </Col>

                {!evolutionConnected && (
                  <Col>
                    <Form.Item name="evolutionQrCode" label="QR Code">
                      <Image
                        preview={false}
                        width={200}
                        src={evolutionQrCodeImage}
                      />
                    </Form.Item>
                  </Col>
                )}
                {evolutionConnected && (
                  <>
                    <Col >
                      <Form.Item label="Número Conectado">
                        <Input value={evolutionConnectedNumber} disabled />
                      </Form.Item>
                    </Col>
                    <Col >
                      <Form.Item style={{ textAlign: 'right', paddingTop: '30px' }}>
                        <Button
                          type="primary"
                          htmlType="button"
                          onClick={handleEvolutionDisconnect}
                          loading={evolutionDisconnecting}
                          disabled={evolutionDisconnecting}
                        >
                          Desconectar WhatsApp
                        </Button>
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>
            </>
          )}
          <Divider>Whatsapp dos clientes</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="onlyVerifiedWhatsappCampaigns"
                label="Enviar campanhas apenas para WhatsApp verificados"
                tooltip="Sobre esta configuração: Quando esta opção estiver ativa, o Acelera Vendas só tentará enviar mensagens de campanha para clientes que tiveram seus números de WhatsApp verificados pelo sistema. Isso garante maior taxa de entrega e reduz mensagens para números inválidos. Recomendação: Mantenha ativo para melhor performance das campanhas."
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Verificação em Massa">
                <Button
                  type="primary"
                  onClick={handleStartMassVerification}
                  loading={startingVerification || isVerificationRunning}
                  disabled={
                    data?.account?.whatsappProvider === 'evolution'
                      ? !evolutionConnected
                      : !plugChatConnected
                  }
                >
                  {isVerificationRunning ? 'Verificação em Andamento...' : 'Verificar Todos os WhatsApp'}
                </Button>
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                  {data?.account?.whatsappProvider === 'evolution'
                    ? (!evolutionConnected
                      ? 'Conecte o WhatsApp para habilitar a verificação em massa'
                      : 'Verifica números de todos os clientes'
                    )
                    : (!plugChatConnected
                      ? 'Conecte o Whatsapp para habilitar a verificação em massa'
                      : 'Verifica números de todos os clientes'
                    )
                  }
                </div>
              </Form.Item>
            </Col>
          </Row>
          {renderVerificationStatus()}
          <Divider>Endereço</Divider>
          <AddressForm
            initialValues={data?.account?.address || {}}
            cities={cities}
            onSearchCity={handleSearchCity}
          />
          <Divider>Configurações de Pesquisa</Divider>
          <Row gutter={16}>
            <Col justify="center" align="middle">
              <Form.Item name="searchLimit" label="Limite de resultados" >
                <InputNumber style={{ width: '50px' }} />
              </Form.Item>
            </Col>
            <Col >
              <Form.Item name="searchPartial" label="Parcial" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col >
              <Form.Item name="anyWord" label="Por qualquer palavra" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="instantSearch"
                label="Busca instantânea nas listagens"
                valuePropName="checked"
                tooltip="Quando ativado, a busca é executada automaticamente conforme você digita. Quando desativado, é necessário clicar no botão pesquisar para executar a busca."
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Divider>Campanhas de Reativação</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="inactiveCustomerDays"
                label="Período para considerar cliente inativo (dias)"
                tooltip="Número de dias desde a última compra para considerar um cliente como inativo nas campanhas de reativação. Padrão: 30 dias"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="30"
                  min={1}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider>Configurações Anti-SPAM WhatsApp</Divider>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item
                name="campaignThrottlingEnabled"
                label="Ativar proteção Anti-SPAM"
                tooltip="Quando ativado, as mensagens de campanhas serão enviadas com intervalos aleatórios para evitar bloqueio do WhatsApp"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>

            <Col span={7}>
              <Form.Item
                name="campaignMinIntervalSeconds"
                label="Intervalo mínimo entre mensagens (segundos)"
                tooltip="Tempo mínimo de espera entre o envio de uma mensagem e outra. Recomendado: 45 segundos"
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    message: 'O intervalo mínimo deve ser maior ou igual a 0'
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="45"
                  min={0}
                  max={300}
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                name="campaignMaxIntervalSeconds"
                label="Intervalo máximo entre mensagens (segundos)"
                tooltip="Tempo máximo de espera entre o envio de uma mensagem e outra. Recomendado: 75 segundos"
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    message: 'O intervalo máximo deve ser maior ou igual a 0'
                  },
                  {
                    validator: (_, value) => {
                      const minValue = form.getFieldValue('campaignMinIntervalSeconds');
                      if (value && minValue && value < minValue) {
                        return Promise.reject(new Error('O intervalo máximo deve ser maior ou igual ao mínimo'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="75"
                  min={0}
                  max={300}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                name="dailyMessageLimit"
                label="Limite diário de mensagens"
                tooltip="Número máximo de mensagens que podem ser enviadas por dia. Recomendação da Meta: 200 mensagens por dia"
                rules={[
                  {
                    type: 'number',
                    min: 1,
                    message: 'O limite deve ser maior que 0'
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="200"
                  min={1}
                  max={1000}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Inteligência Artificial</Divider>
          <Row ref={aiPromptRef} gutter={16}>
            <Col span={24}>
              <Form.Item
                name="aiPromptTemplate"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Instruções para IA (para personalização de mensagens)
                    {promptAssistantButton('ai_prompt_template', 'Instruções para IA')}
                  </div>
                }
                tooltip="Configure instruções específicas para que a IA gere mensagens personalizadas. Descreva seu negócio, tom de comunicação desejado (formal/informal), e como a IA deve se comunicar com seus clientes."
              >
                <Input.TextArea
                  rows={6}
                  placeholder="Ex: Você é um assistente de vendas para uma loja de móveis. Use linguagem informal e amigável. Sempre mencione nossos valores de qualidade e prazo de entrega. Cumprimente o cliente pelo nome e seja objetivo."
                />
              </Form.Item>
            </Col>
          </Row>
          <Row ref={campaignsRef} gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="campaignOfferPromptTemplate"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Prompt para campanhas de oferta
                    {promptAssistantButton('campaign_offer_prompt_template', 'Prompt para campanhas de oferta')}
                  </div>
                }
                tooltip="Instruções específicas para mensagens de oferta de produtos."
              >
                <Input.TextArea
                  rows={5}
                  placeholder="Ex: Destaque os produtos selecionados, benefícios, condições comerciais e incentive o cliente a aproveitar a oferta."
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="campaignReactivationPromptTemplate"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Prompt para campanhas de reativação
                    {promptAssistantButton('campaign_reactivation_prompt_template', 'Prompt para campanhas de reativação')}
                  </div>
                }
                tooltip="Instruções específicas para mensagens destinadas a clientes inativos."
              >
                <Input.TextArea
                  rows={5}
                  placeholder="Ex: Reforce que sentimos falta do cliente, mencione histórico de compra quando disponível e convide para voltar a comprar."
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="campaignActivationPromptTemplate"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Prompt para campanhas de ativação
                    {promptAssistantButton('campaign_activation_prompt_template', 'Prompt para campanhas de ativação')}
                  </div>
                }
                tooltip="Instruções específicas para mensagens de boas-vindas ou primeira compra."
              >
                <Input.TextArea
                  rows={5}
                  placeholder="Ex: Dê boas-vindas ao cliente, explique como comprar e incentive o primeiro pedido."
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="campaignFollowUpPromptTemplate"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Prompt para campanhas de follow-up
                    {promptAssistantButton('campaign_follow_up_prompt_template', 'Prompt para campanhas de follow-up')}
                  </div>
                }
                tooltip="Instruções específicas para mensagens de acompanhamento."
              >
                <Input.TextArea
                  rows={5}
                  placeholder="Ex: Retome o assunto anterior, seja objetivo e pergunte como pode ajudar no próximo passo."
                />
              </Form.Item>
            </Col>
          </Row>
          <Row ref={serviceRef} gutter={16}>
            <Col span={24}>
              <Form.Item
                name="servicePromptTemplate"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Instruções para IA de Serviços (para qualificação de leads)
                    {promptAssistantButton('service_prompt_template', 'Instruções para IA de Serviços')}
                    <Tooltip title="Clique para preencher com um template padrão de instruções">
                      <Button
                        size="small"
                        type="link"
                        onClick={() => handleFillServicePromptTemplate()}
                        style={{ padding: '0 4px', height: 'auto', fontSize: '12px' }}
                      >
                        <QuestionCircleOutlined />
                      </Button>
                    </Tooltip>
                  </div>
                }
                tooltip="Configure instruções específicas para qualificação de leads de serviços. Use {{company_name}} para inserir o nome da empresa automaticamente. Defina critérios de qualificação e link do Calendly."
              >
                <Input.TextArea
                  rows={8}
                  placeholder="Ex: Você é um assistente de pré-vendas da {{company_name}}. Qualifique leads perguntando sobre tipo de negócio, segmento, vendas e usuários. Link: https://calendly.com/sua-empresa/demo"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" loading={isSaveLoading} disabled={isSaveLoading} style={{ width: '200px' }}>
              Atualizar conta
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'integrations',
      label: 'Integrações',
      children: (
        <Form
          form={form}
          initialValues={data?.account}
          onFinish={handleUpdate}
          layout="vertical"
        >
          <Divider>Pipedrive</Divider>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="tokenPipedrive"
                label="Token do Pipedrive"
                tooltip="Token de API para integração automática de leads qualificados"
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Cole aqui seu token de API"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="pipedriveStageId"
                label="Funil e etapa de negócio no Pipedrive"
                tooltip="Selecione a etapa do funil de negócio no Pipedrive onde os leads qualificados serão criados como deals"
              >
                <Select
                  placeholder="Selecione um stage"
                  allowClear
                  loading={pipedriveStagesLoading}
                  disabled={!form.getFieldValue('tokenPipedrive')}
                  onFocus={handleLoadPipedriveStages}
                  options={pipedriveStages.map(stage => ({
                    label: `${stage.pipelineName} - ${stage.name}`,
                    value: stage.id.toString(),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Configuração de Webhook Pipedrive</Divider>
          <Row gutter={16}>
            <Col span={24}>
              <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
                <h4>Credenciais para Webhook</h4>
                <p>Use estas credenciais para configurar o webhook no Pipedrive:</p>

                <Row gutter={16} style={{ marginBottom: 8 }}>
                  <Col span={12}>
                    <Form.Item label="Username">
                      <Input
                        value={data?.account?.pipedriveUsername || 'Não gerado'}
                        readOnly
                        style={{ backgroundColor: '#fff' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Password">
                      <Input.Password
                        value={data?.account?.pipedrivePassword || 'Não gerado'}
                        readOnly
                        style={{ backgroundColor: '#fff' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Button
                  type="primary"
                  onClick={handleGeneratePipedriveCredentials}
                  loading={generatingCredentials}
                  style={{ marginBottom: 16 }}
                >
                  Gerar Novas Credenciais
                </Button>

                <div style={{ marginTop: 16, padding: 12, backgroundColor: '#e6f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                  <h5>Como configurar no Pipedrive:</h5>
                  <ol>
                    <li>Acesse Configurações &gt; Webhooks no seu Pipedrive</li>
                    <li>Clique em "Adicionar webhook"</li>
                    <li>URL do webhook:
                      <Input.Group compact style={{ marginTop: 8, display: 'flex' }}>
                        <Input
                          value="https://api.aceleravenda.com.br/api/v1/pipedrive/webhook"
                          readOnly
                          style={{ flex: 1 }}
                        />
                        <Button
                          icon={<CopyOutlined />}
                          onClick={() => {
                            navigator.clipboard.writeText('https://api.aceleravenda.com.br/api/v1/pipedrive/webhook');
                            message.success('URL copiada para a área de transferência!');
                          }}
                          title="Copiar URL"
                        />
                      </Input.Group>
                    </li>
                    <li>Método HTTP: POST</li>
                    <li>Eventos: Selecione "Person added" e "Person updated"</li>
                    <li>Autenticação: HTTP Basic Auth</li>
                    <li>Username: Use o valor do campo "Username" acima</li>
                    <li>Password: Use o valor do campo "Password" acima</li>
                  </ol>
                </div>
              </div>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" loading={isSaveLoading} disabled={isSaveLoading} style={{ width: '200px' }}>
              Atualizar conta
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'campaign-hours',
      label: 'Horários das Campanhas',
      children: (
        <CampaignHoursSettings
          account={data?.account}
        />
      )
    }
  ];

  return (
    <Row justify="center" align="middle" style={{ height: '100%' }}>
      {messageContextHolder}
      {modalContextHolder}
      <Col span={24}>
        <Card>
          <h2>Dados da conta</h2>
          <Tabs
            defaultActiveKey="general"
            items={tabItems}
            size="large"
          />
        </Card>
      </Col>
      <Tour open={tourOpen} onClose={() => setTourOpen(false)} steps={tourSteps} />
      <Modal
        title={`Assistente de prompt - ${promptAssistantLabel}`}
        open={promptAssistantOpen}
        onCancel={closePromptAssistant}
        onOk={handleGeneratePromptFromAnswers}
        okText="Gerar prompt"
        cancelText="Cancelar"
        confirmLoading={generatingPromptTemplate}
        width={800}
        maskClosable={false}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Responda as perguntas abaixo para gerar automaticamente um prompt personalizado com IA."
          description="Após gerar, revise o texto preenchido no campo e clique em Atualizar conta para salvar as demais alterações da tela, se necessário."
        />
        <Form form={promptAssistantForm} layout="vertical">
          {loadingPromptQuestions ? (
            <p>Carregando perguntas...</p>
          ) : (
            promptQuestions.map((question) => (
              <Form.Item
                key={question.id}
                name={`question_${question.id}`}
                label={question.question}
                rules={[{ required: true, message: 'Responda esta pergunta' }]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            ))
          )}
        </Form>
      </Modal>
    </Row>
  );
};

export default AccountSettings;